import React, { useState, useRef, useMemo, useCallback, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { AgGridReact } from "ag-grid-react";
import { FiEdit2, FiEye } from "react-icons/fi";
import { MdDeleteForever } from "react-icons/md";
import { toast } from "react-toastify";

import SearchFilter from "../../../Common/SearchFilter";
import ColumnVisibilityControl from "../../../Common/ColumnVisibilityControl";
import ActionButton from "../../../Common/ActionButton";
import StatusChip, { statusChipType } from "../../../Common/StatusChip";
import DeleteConfirmModal from "../../../../Common/DeleteConfirmModal";
import DemandNoteDetailsModal from "./DemandNoteDetailsModal";
import { getDemandNotes, getDemandNote, deleteDemandNote } from "./demandNoteService";

/**
 * Demand Note List Table
 * Matches the layout, styling, search filter, column visibility,
 * and pagination of StocksTable.
 */
const DemandNoteTable = () => {
  const history = useHistory();

  const gridRef = useRef(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [columnVisibility, setColumnVisibility] = useState({});
  const [demandNotes, setDemandNotes] = useState([]);
  const [selectedNote, setSelectedNote] = useState(null);
  const [viewNote, setViewNote] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Load demand notes from the API
  const loadNotes = useCallback(async () => {
    setDemandNotes(await getDemandNotes());
  }, []);

  useEffect(() => {
    loadNotes();
  }, [loadNotes]);

  // Client-side quick filter
  const filteredNotes = useMemo(() => {
    if (!searchTerm) {
      return demandNotes;
    }
    const lower = searchTerm.toLowerCase();
    return demandNotes.filter((item) =>
      [
        item?.DemandNoteNo || "",
        item?.RequestingSite || "",
        item?.Priority || "",
        item?.RequestedBy || "",
        item?.FirstApprover || "",
        item?.Status || "",
        item?.RequisitionDate || "",
        item?.ExpectedDeliveryDate || "",
        ...(item?.Items || []).map((i) => i?.MaterialName || ""),
      ].some((val) => String(val).toLowerCase().includes(lower)),
    );
  }, [demandNotes, searchTerm]);

  // Handle delete
  const handleConfirmDelete = async () => {
    if (!selectedNote) {
      return;
    }
    setIsDeleting(true);
    try {
      const res = await deleteDemandNote(selectedNote.Id);
      if (!res.ok) {
        throw new Error(res.message);
      }
      await loadNotes();
      toast.success(`Demand note "${selectedNote.DemandNoteNo}" deleted successfully`);
    } catch (err) {
      toast.error(err.message || "Failed to delete demand note");
    } finally {
      setIsDeleting(false);
      setSelectedNote(null);
    }
  };

  const addPath = history.location.pathname.startsWith("/store/material-requisition")
    ? "/store/material-requisition/add"
    : history.location.pathname.startsWith("/store/demand-note")
      ? "/store/demand-note/add"
      : history.location.pathname.startsWith("/procurement")
        ? "/procurement/demand-note/add"
        : "/store/demand-note/add";

  /* ─── Column Definitions ──────────────────────────────────────────────── */
  const columnDefs = useMemo(
    () => [
      {
        headerName: "Sr",
        colId: "sr",
        valueGetter: "node.rowIndex + 1",
        width: 60,
        minWidth: 60,
        maxWidth: 70,
        suppressSizeToFit: true,
        filter: false,
        sortable: false,
        cellStyle: { textAlign: "center" },
      },
      {
        headerName: "Demand Note No",
        field: "DemandNoteNo",
        flex: 1.2,
        minWidth: 140,
        cellRenderer: (params) => (
          <span style={{ fontWeight: 600, color: "#056363" }}>{params.value}</span>
        ),
      },
      {
        headerName: "Requesting Site",
        field: "RequestingSite",
        flex: 1.2,
        minWidth: 140,
      },
      {
        headerName: "Requisition Date",
        field: "RequisitionDate",
        flex: 1,
        minWidth: 120,
      },
      {
        headerName: "Expected Delivery",
        field: "ExpectedDeliveryDate",
        flex: 1,
        minWidth: 130,
      },
      {
        headerName: "Priority",
        field: "Priority",
        flex: 1,
        minWidth: 110,
        cellRenderer: (params) => {
          const val = params.value || "Medium";
          const priorityType =
            val === "High / Urgent" || val === "High"
              ? "error"
              : val === "Medium"
                ? "warning"
                : "info";
          return <StatusChip status={val} type={priorityType} />;
        },
      },
      {
        headerName: "Requested By",
        field: "RequestedBy",
        flex: 1.2,
        minWidth: 150,
      },
      {
        headerName: "Items",
        colId: "itemsCount",
        flex: 0.8,
        minWidth: 80,
        valueGetter: (params) => params.data?.Items?.length || 0,
        cellRenderer: (params) => (
          <span className="badge badge-light border px-2 py-1" style={{ fontSize: "12px" }}>
            {params.value} {params.value === 1 ? "item" : "items"}
          </span>
        ),
      },
      {
        headerName: "Status",
        field: "Status",
        flex: 1,
        minWidth: 130,
        cellRenderer: (params) => {
          const status = params.value || "Pending Approval";
          return <StatusChip status={status} type={statusChipType(status)} />;
        },
      },
      {
        headerName: "Actions",
        colId: "actions",
        width: 140,
        filter: false,
        sortable: false,
        suppressMenu: true,
        headerComponent: () => (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              width: "100%",
              height: "100%",
            }}
          >
            <ActionButton text="New+" newBtn="true" onClick={() => history.push(addPath)} />
          </div>
        ),
        cellRenderer: (params) => (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
              gap: "12px",
            }}
          >
            <FiEye
              title="View Details"
              size="1.15em"
              style={{ cursor: "pointer", color: "#0284c7" }}
              onClick={async (e) => {
                e.stopPropagation();
                setViewNote(await getDemandNote(params.data.Id));
              }}
            />
            {/* The backend only allows edit/delete before any approval decision */}
            {params.data?.Status === "Pending Approval" && !params.data?.HasDecision && (
              <>
                <FiEdit2
                  title="Edit"
                  size="1.1em"
                  style={{ cursor: "pointer", color: "#4e545a" }}
                  onClick={async (e) => {
                    e.stopPropagation();
                    const full = await getDemandNote(params.data.Id);
                    if (full) {
                      history.push(addPath, { ...full, isEdit: true });
                    }
                  }}
                />
                <MdDeleteForever
                  title="Delete"
                  size="1.2em"
                  className="cur_sor"
                  style={{ cursor: "pointer", color: "#c0392b" }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedNote(params.data);
                  }}
                />
              </>
            )}
          </div>
        ),
      },
    ],
    [history, addPath],
  );

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      filter: true,
      resizable: true,
      tooltipValueGetter: (p) => p.value,
      valueFormatter: (p) =>
        p.value === null || p.value === undefined || p.value === "" ? "N/A" : p.value,
      minWidth: 100,
    }),
    [],
  );

  /* ─── Column show / hide ──────────────────────────────────────────────── */
  const onGridReady = useCallback((params) => {
    const map = {};
    params.api.getColumns()?.forEach((col) => {
      if (col.getColDef().headerName) {
        map[col.getColId()] = col.isVisible();
      }
    });
    setColumnVisibility(map);
  }, []);

  const toggleColumn = useCallback(
    (colId) => {
      if (!gridRef.current?.api) {
        return;
      }
      const isCurrentlyVisible = columnVisibility[colId];
      gridRef.current.api.setColumnsVisible([colId], !isCurrentlyVisible);
      setColumnVisibility((prev) => ({ ...prev, [colId]: !isCurrentlyVisible }));
    },
    [columnVisibility],
  );

  /* ─── Render ──────────────────────────────────────────────────────────── */
  return (
    <>
      {/* Toolbar */}
      <div
        className="d-flex flex-wrap align-items-center justify-content-end pt-3 app_font w-100"
        style={{ gap: "12px", width: "100%" }}
      >
        <div
          className="d-flex align-items-center"
          style={{
            minHeight: "30px",
            maxWidth: "100%",
            marginRight: "auto",
            padding: "4px 12px",
            color: "var(--btn-comp-text, #0b6462)",
            fontSize: "var(--font-md)",
            fontWeight: 600,
            whiteSpace: "nowrap",
          }}
          title="Material Requisition"
        >
          Material Requisition
        </div>
        <SearchFilter
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search Demand Notes..."
        />

        <ColumnVisibilityControl
          gridApi={gridRef.current?.api}
          columnVisibility={columnVisibility}
          toggleColumn={toggleColumn}
        />
      </div>

      {/* Grid */}
      <div className="row">
        <div className="col-12 pr-0 pl-0">
          <div className="ag-theme-alpine vouchers_table_height3 mt-3" style={{ width: "100%" }}>
            <AgGridReact
              ref={gridRef}
              rowData={filteredNotes || []}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              quickFilterText={searchTerm}
              pagination={true}
              paginationPageSize={15}
              tooltipShowDelay={0}
              onGridReady={onGridReady}
              rowStyle={{ cursor: "pointer" }}
              onRowClicked={async (e) => {
                if (!e.event.target.closest("button, svg")) {
                  setViewNote(await getDemandNote(e.data.Id));
                }
              }}
            />
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmModal
        open={!!selectedNote}
        onClose={() => setSelectedNote(null)}
        onConfirm={handleConfirmDelete}
        loading={isDeleting}
        message={`Are you sure you want to delete Demand Note "${selectedNote?.DemandNoteNo}"?`}
        heading="Delete Demand Note"
      />

      {/* View Details Modal */}
      <DemandNoteDetailsModal
        open={!!viewNote}
        onClose={() => setViewNote(null)}
        note={viewNote}
        onChanged={loadNotes}
      />
    </>
  );
};

export { DemandNoteTable };
export default DemandNoteTable;
